import { z } from 'zod';
/**
 * RFC 9457 Problem Details — the wire format for every error this API returns.
 *
 * A standard rather than a house envelope, so the API is consumable by something
 * that was not written for it. See spec §13.7.
 *
 * The division that matters: **`type` is the contract, `title` and `detail` are
 * not.** `type` carries an event code from `packages/observability` — typed,
 * enumerable, and the same word the logs and traces use for the same thing. The
 * prose may change freely and clients must never parse it.
 */
/** Codes are SCREAMING_SNAKE so they read as identifiers, never as sentences. */
export const errorCodeSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]*$/, 'must be an event code, e.g. CONTENT_NOT_FOUND');
/** One field-level problem. Present when a payload failed validation. */
export const problemDetailSchema = z.object({
    /** Dotted path to the offending field, e.g. `body.title`. */
    path: z.string(),
    /** Why it was rejected, in developer English. */
    message: z.string(),
});
export const problemDocumentSchema = z.object({
    /**
     * The event code. Named `type` because RFC 9457 calls it that; it is a stable
     * token, not a URI to dereference.
     */
    type: errorCodeSchema,
    /** Short, human-readable summary. Prose — do not parse. */
    title: z.string(),
    status: z.number().int().min(400).max(599),
    /** Longer explanation of this specific occurrence. Prose — do not parse. */
    detail: z.string(),
    /**
     * The correlation id for the request that failed. RFC 9457 calls this member
     * `instance`; here it is what ties a user's complaint to a trace and a log line.
     */
    instance: z.string(),
    // ---- extension members ----
    /** Field-level problems. Absent unless validation failed. */
    details: z.array(problemDetailSchema).optional(),
    /**
     * Whether retrying the identical request could succeed. Data rather than
     * inference: a client should not have to guess from the status code, and a 503
     * caused by a bad payload is not the same as one caused by a cold dependency.
     */
    retryable: z.boolean(),
    /** When a spent daily allowance starts again. Present only on that refusal. */
    resetAt: z.iso.datetime().optional(),
});
/** The media type RFC 9457 defines. Not `application/json`. */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
//# sourceMappingURL=error.js.map